import { useRef, useState, useEffect, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Stars, OrbitControls, Text } from '@react-three/drei';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';

// Global CSS reset to override default app templates (Vite/CRA) constraining layout width
const GlobalStyles = ({ allowScroll = false }) => (
  <style>{`
    *, *::before, *::after {
      box-sizing: border-box;
      margin: 0;
      padding: 0;
    }
    html, body, #root, #root > div {
      width: 100vw !important;
      height: ${allowScroll ? '100dvh' : '100vh'} !important;
      min-height: 100dvh !important;
      max-width: 100vw !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow-x: hidden !important;
      overflow-y: ${allowScroll ? 'auto' : 'hidden'} !important;
      background: transparent !important;
    }
    canvas {
      width: 100% !important;
      height: 100% !important;
      display: block !important;
    }
  `}</style>
);

function getViewportProfile() {
  if (window.innerWidth <= 640) return 'phone';
  if (window.innerWidth <= 1024) return 'tablet';
  return 'desktop';
}

function useViewportProfile() {
  const [viewportProfile, setViewportProfile] = useState(getViewportProfile);

  useEffect(() => {
    const handleResize = () => {
      setViewportProfile(getViewportProfile());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewportProfile;
}

// 1. Reusable Thruster Flame Component
function ThrusterFlame({ offset, boostActive }) {
  const flameRef = useRef();

  useFrame((state) => {
    const t = state.clock.getElapsedTime();
    const pulse = Math.sin(t * 30) * 0.2;
    const boostMultiplier = boostActive ? 2.5 : 1.0;
    
    if (flameRef.current) {
      flameRef.current.scale.set(
        boostMultiplier, 
        boostMultiplier, 
        (1 + pulse) * boostMultiplier
      );
    }
  });

  return (
    <group position={[offset[0], -0.1, -2.0]}>
      <mesh ref={flameRef} rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.2, 1.2, 8]} />
        <meshBasicMaterial color="#00ffff" transparent opacity={0.9} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} scale={[1.3, 1.3, 1.5]}>
        <coneGeometry args={[0.25, 0.8, 8]} />
        <meshBasicMaterial color="#ffffff" transparent opacity={0.6} />
      </mesh>
    </group>
  );
}

// 2. Spaceship Component
function Spaceship({ onArrived, onProgress, externalBoosting = false }) {
  const shipRef = useRef();
  const [isBoosting, setIsBoosting] = useState(false);
  const travelProgress = useRef(0);
  
  const keys = useRef({ left: false, right: false, up: false, down: false });
  const steeringPos = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') setIsBoosting(true);
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keys.current.left = true;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keys.current.right = true;
      if (e.code === 'ArrowUp' || e.code === 'KeyW') keys.current.up = true;
      if (e.code === 'ArrowDown' || e.code === 'KeyS') keys.current.down = true;
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space') setIsBoosting(false);
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') keys.current.left = false;
      if (e.code === 'ArrowRight' || e.code === 'KeyD') keys.current.right = false;
      if (e.code === 'ArrowUp' || e.code === 'KeyW') keys.current.up = false;
      if (e.code === 'ArrowDown' || e.code === 'KeyS') keys.current.down = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  const visualBoostActive = isBoosting || externalBoosting;

  useFrame((state, delta) => {
    const t = state.clock.getElapsedTime();
    const boostActive = isBoosting || externalBoosting;

    if (boostActive) {
      travelProgress.current = Math.min(1, travelProgress.current + delta * 0.12);
    } else {
      travelProgress.current = Math.max(0, travelProgress.current - delta * 0.08);
    }

    if (onProgress) {
      onProgress(travelProgress.current);
    }

    if (travelProgress.current >= 0.99) {
      onArrived(true);
    } else {
      onArrived(false);
    }

    const speed = delta * 4;
    if (keys.current.left) steeringPos.current.x -= speed;
    if (keys.current.right) steeringPos.current.x += speed;
    if (keys.current.up) steeringPos.current.y += speed;
    if (keys.current.down) steeringPos.current.y -= speed;

    steeringPos.current.x = Math.max(-4, Math.min(4, steeringPos.current.x));
    steeringPos.current.y = Math.max(-3, Math.min(3, steeringPos.current.y));

    const startZ = 0;
    const earthZ = -180;
    const currentZ = startZ + (earthZ - startZ) * travelProgress.current;

    if (shipRef.current) {
      shipRef.current.position.x = 6 * travelProgress.current + steeringPos.current.x + Math.sin(t * 1.2) * 0.05;
      shipRef.current.position.y = 4 * travelProgress.current + steeringPos.current.y + Math.sin(t * 1.8) * 0.05;
      shipRef.current.position.z = currentZ;

      shipRef.current.rotation.y = Math.PI - (steeringPos.current.x * 0.15);
      shipRef.current.rotation.z = -steeringPos.current.x * 0.25;
      shipRef.current.rotation.x = 0.15 + (steeringPos.current.y * 0.15);
    }
  });

  return (
    <group ref={shipRef} rotation={[0.15, Math.PI, 0]}>
      {/* Hull */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <coneGeometry args={[0.6, 3.5, 6]} />
        <meshStandardMaterial color="#eeeeee" roughness={0.3} metalness={0.2} />
      </mesh>

      {/* Sleek Contoured Windshield Window */}
      <mesh position={[0, 0.22, 0.35]} rotation={[0.38, 0, 0]}>
        <boxGeometry args={[0.32, 0.06, 0.85]} />
        <meshStandardMaterial 
          color="#00aaff" 
          roughness={0.1} 
          metalness={0.1} 
          emissive="#004488" 
          transparent 
          opacity={0.85} 
        />
      </mesh>

      {/* Wings */}
      <mesh position={[0, -0.1, -0.3]} rotation={[0, 0, 0]}>
        <boxGeometry args={[3.2, 0.1, 1.2]} />
        <meshStandardMaterial color="#e63946" roughness={0.4} metalness={0.2} />
      </mesh>

      {/* Left Engine */}
      <mesh position={[-0.4, -0.1, -1.6]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.2, 0.3, 0.8, 8]} />
        <meshStandardMaterial color="#555555" roughness={0.5} metalness={0.2} />
      </mesh>

      {/* Right Engine */}
      <mesh position={[0.4, -0.1, -1.6]} rotation={[Math.PI / 2, 0, 0]}>
        <cylinderGeometry args={[0.2, 0.3, 0.8, 8]} />
        <meshStandardMaterial color="#555555" roughness={0.5} metalness={0.2} />
      </mesh>

      {/* Rear marking */}
      <Text
        position={[0, 0.34, -1.92]}
        rotation={[0.25, Math.PI, 0]}
        fontSize={0.22}
        color="#ffffff"
        anchorX="center"
        anchorY="middle"
        outlineWidth={0.012}
        outlineColor="#e63946"
        renderOrder={20}
      >
        USA
      </Text>

      <ThrusterFlame offset={[-0.4]} boostActive={visualBoostActive} />
      <ThrusterFlame offset={[0.4]} boostActive={visualBoostActive} />
    </group>
  );
}

function ThrustersButton({ onBoostChange, bottomOffset = '96px' }) {
  const setBoost = (active) => (event) => {
    event.preventDefault();
    onBoostChange(active);
  };

  return (
    <button
      type="button"
      aria-label="Hold thrusters"
      style={{
        position: 'absolute',
        left: '50%',
        bottom: bottomOffset,
        transform: 'translateX(-50%)',
        zIndex: 18,
        width: '118px',
        height: '64px',
        borderRadius: '16px',
        border: '1px solid rgba(255,255,255,0.45)',
        background: 'rgba(26, 104, 209, 0.82)',
        color: '#ffffff',
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: 0,
        touchAction: 'none',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        boxShadow: '0 0 18px rgba(77,166,255,0.3)',
      }}
      onPointerDown={setBoost(true)}
      onPointerUp={setBoost(false)}
      onPointerCancel={setBoost(false)}
      onPointerLeave={setBoost(false)}
    >
      THRUSTERS
    </button>
  );
}

// 3. Earth Component
function createEarthTexture() {
  const canvas = document.createElement('canvas');
  canvas.width = 1024;
  canvas.height = 512;
  const ctx = canvas.getContext('2d');

  const oceanGradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  oceanGradient.addColorStop(0, '#0b4f9f');
  oceanGradient.addColorStop(0.45, '#136fbd');
  oceanGradient.addColorStop(1, '#062f78');
  ctx.fillStyle = oceanGradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  const drawLand = (points, color = '#2f8f46') => {
    ctx.beginPath();
    points.forEach(([x, y], index) => {
      if (index === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.bezierCurveTo(
          x - 28,
          y - 18,
          x + 30,
          y + 18,
          x,
          y
        );
      }
    });
    ctx.closePath();
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = 'rgba(188, 214, 148, 0.45)';
    ctx.lineWidth = 6;
    ctx.stroke();
  };

  drawLand([[170, 120], [260, 95], [330, 150], [295, 245], [205, 260], [145, 205]]);
  drawLand([[315, 285], [380, 330], [350, 430], [285, 470], [245, 395]], '#3f9b43');
  drawLand([[520, 125], [655, 115], [760, 185], [735, 285], [590, 300], [500, 235]], '#4d9f45');
  drawLand([[660, 305], [735, 345], [705, 430], [625, 455], [585, 370]], '#a58d45');
  drawLand([[790, 240], [880, 250], [935, 320], [890, 380], [805, 350]], '#368f4b');
  drawLand([[420, 78], [485, 55], [530, 90], [500, 128], [430, 120]], '#8da744');

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.anisotropy = 8;
  return texture;
}

function Earth({ position = [-25, -18, -200], scale = 1 }) {
  const earthRef = useRef();
  const earthTexture = useMemo(() => createEarthTexture(), []);

  useFrame((_, delta) => {
    if (earthRef.current) {
      earthRef.current.rotation.y += delta * 0.04;
    }
  });

  return (
    <group position={position} scale={scale}>
      <group position={[0, 23, 0]} rotation={[0, 0, -0.55]}>
        <Text
          position={[-9, 6, 0]}
          fontSize={3.4}
          color="#ffffff"
          anchorX="center"
          anchorY="middle"
          outlineWidth={0.12}
          outlineColor="#1a68d1"
        >
          Home
        </Text>
        <mesh position={[0, 0, 0]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.22, 0.22, 14, 16]} />
          <meshBasicMaterial color="#ffffff" />
        </mesh>
        <mesh position={[6.5, -4.5, 0]} rotation={[0, 0, -0.55]}>
          <coneGeometry args={[1.05, 2.4, 24]} />
          <meshBasicMaterial color="#ffffff" />
        </mesh>
      </group>
      <group ref={earthRef}>
        <mesh>
          <sphereGeometry args={[14, 64, 64]} />
          <meshStandardMaterial
            map={earthTexture}
            color="#1b75d0"
            emissive="#082b61"
            emissiveIntensity={0.18}
            roughness={0.9}
            metalness={0}
          />
        </mesh>
      </group>
      <mesh scale={[1.04, 1.04, 1.04]}>
        <sphereGeometry args={[14, 64, 64]} />
        <meshStandardMaterial
          color="#2aa8ff"
          transparent={true}
          opacity={0.14}
          blending={THREE.AdditiveBlending}
          side={THREE.BackSide}
          depthWrite={false}
        />
      </mesh>
    </group>
  );
}

// 4. Main HeroScene Component
export default function HeroScene() {
  const [hasArrived, setHasArrived] = useState(false);
  const [isFading, setIsFading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [touchBoosting, setTouchBoosting] = useState(false);
  const viewportProfile = useViewportProfile();
  const navigate = useNavigate();

  const sceneSettings = {
    phone: {
      camera: { position: [0, 2.3, 7], fov: 72, far: 1000 },
      earthPosition: [0, -11, -95],
      earthScale: 0.78,
    },
    tablet: {
      camera: { position: [0, 2.6, 7], fov: 68, far: 1000 },
      earthPosition: [0, -12, -112],
      earthScale: 0.9,
    },
    desktop: {
      camera: { position: [5, 5, 6], fov: 55, far: 1000 },
      earthPosition: [-25, -18, -200],
      earthScale: 1,
    },
  }[viewportProfile];
  const showThrustersButton = viewportProfile !== 'desktop';
  const sceneHeight = showThrustersButton ? '100dvh' : '100vh';
  const thrustersButtonBottom = viewportProfile === 'phone' ? '230px' : '96px';

  const handleArrived = (arrived) => {
    if (arrived && !hasArrived) {
      setHasArrived(true);
      setTimeout(() => {
        setIsFading(true);
      }, 300);
      setTimeout(() => {
        navigate('/home');
      }, 800); 
    }
  };

  return (
    <div style={{
      width: '100vw',
      minHeight: sceneHeight,
      height: sceneHeight,
      background: 'transparent',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: showThrustersButton ? 'auto' : 'hidden'
    }}>
      <GlobalStyles allowScroll={showThrustersButton} />
      
      {/* Unboxed Transparent Overlay */}
      {!hasArrived && (
        <div style={{ 
          position: 'absolute', 
          top: '20px', 
          left: 0,
          right: 0,
          margin: '0 auto',
          width: 'fit-content',
          maxWidth: '90%',
          color: 'white', 
          fontFamily: 'sans-serif', 
          zIndex: 10,
          pointerEvents: 'none',
          textAlign: 'center',
          background: 'transparent',
          boxShadow: 'none',
          border: 'none'
        }}>
          <div style={{
            fontSize: '1.35rem',
            fontWeight: 'bold',
            color: progress >= 0.65 ? '#4da6ff' : '#ffcc00',
            textShadow: '0 0 12px rgba(0,255,255,0.6)',
            marginBottom: '8px',
            transition: 'color 0.3s ease-in-out'
          }}>
            {progress >= 0.65 
              ? "Yes, you got this! We are arriving on Earth!" 
              : "Add thrusters, pilot! We are almost there"}
          </div>

          <p style={{ margin: '0 0 4px 0', opacity: 0.7, fontSize: '0.9rem' }}>Use <b>W/A/S/D</b> to steer</p>
          <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>
            Hold <b>{showThrustersButton ? 'Thrusters' : 'Spacebar'}</b> for thrusters
          </p>
        </div>
      )}

      {!hasArrived && showThrustersButton && (
        <ThrustersButton
          onBoostChange={setTouchBoosting}
          bottomOffset={thrustersButtonBottom}
        />
      )}

      {hasArrived && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          zIndex: 20,
          animation: 'fadeIn 0.5s ease-in-out'
        }}>
          <h1 style={{ 
            fontSize: '3rem', 
            color: '#1a68d1', 
            textShadow: '0 0 20px rgba(77, 166, 255, 0.9)',
            fontFamily: 'sans-serif',
            margin: 0
          }}>
            Welcome home Explorer!
          </h1>
        </div>
      )}

      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: '#ffffff',
        zIndex: 30,
        opacity: isFading ? 1 : 0,
        pointerEvents: 'none',
        transition: 'opacity 0.5s ease-in-out'
      }} />

      <Canvas shadows={false} camera={sceneSettings.camera}>
        <color attach="background" args={['#050505']} />
        <ambientLight intensity={1.2} />
        <directionalLight position={[10, 20, 15]} intensity={2.0} />
        <directionalLight position={[-10, -10, -10]} intensity={0.5} />

        <Stars radius={250} depth={200} count={12000} factor={4} saturation={0} fade speed={1} />
        
        <Spaceship
          onArrived={handleArrived}
          onProgress={setProgress}
          externalBoosting={touchBoosting}
        />
        <Earth position={sceneSettings.earthPosition} scale={sceneSettings.earthScale} />

        <OrbitControls enableZoom={false} enablePan={false} maxPolarAngle={Math.PI / 2 + 0.1} minPolarAngle={Math.PI / 2 - 0.2} />
      </Canvas>
    </div>
  );
}
